const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;
const REDIRECT_URI = import.meta.env.VITE_COGNITO_REDIRECT_URI;
const LOGOUT_REDIRECT_URI = import.meta.env.VITE_COGNITO_LOGOUT_REDIRECT_URI;
const COGNITO_REGION = import.meta.env.VITE_COGNITO_REGION;
const TOKEN_KEY = 'glazier_access_token';
const REFRESH_TOKEN_KEY = 'glazier_refresh_token';
const USER_KEY = 'glazier_user';
const CODE_VERIFIER_KEY = 'glazier_code_verifier';
/**
 * Generate a random string for PKCE code verifier
 */
function generateCodeVerifier() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    for (let i = 0; i < 128; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
}
/**
 * Generate PKCE code challenge from verifier using SHA-256
 */
async function generateCodeChallenge(verifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const bytes = new Uint8Array(digest);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export class AuthService {
    /**
     * Get the Cognito login URL for redirecting user to hosted UI with PKCE
     */
    static async getLoginUrl() {
        const verifier = generateCodeVerifier();
        const challenge = await generateCodeChallenge(verifier);
        // Store verifier for later use in token exchange
        localStorage.setItem(CODE_VERIFIER_KEY, verifier);
        const params = new URLSearchParams({
            client_id: CLIENT_ID,
            response_type: 'code',
            scope: 'openid email profile',
            redirect_uri: REDIRECT_URI,
            code_challenge: challenge,
            code_challenge_method: 'S256',
        });
        return `https://${COGNITO_DOMAIN}.auth.${COGNITO_REGION}.amazoncognito.com/oauth2/authorize?${params.toString()}`;
    }
    /**
     * Get the Cognito logout URL
     */
    static getLogoutUrl() {
        const params = new URLSearchParams({
            client_id: CLIENT_ID,
            logout_uri: LOGOUT_REDIRECT_URI,
        });
        return `https://${COGNITO_DOMAIN}.auth.${COGNITO_REGION}.amazoncognito.com/logout?${params.toString()}`;
    }
    /**
     * Exchange authorization code for tokens using backend endpoint
     */
    static async exchangeCodeForTokens(code) {
        const verifier = localStorage.getItem(CODE_VERIFIER_KEY);
        console.log('[AuthService] Attempting token exchange with code:', code.substring(0, 20) + '...');
        console.log('[AuthService] Code verifier exists:', !!verifier);
        if (!verifier) {
            console.error('[AuthService] Code verifier not found in localStorage');
            return false;
        }
        try {
            const apiUrl = import.meta.env.VITE_API_URL;
            const exchangeUrl = `${apiUrl}/auth/exchange`;
            console.log('[AuthService] Exchange URL:', exchangeUrl);
            const response = await fetch(exchangeUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    code: code,
                    code_verifier: verifier,
                }),
            });
            console.log('[AuthService] Token exchange response status:', response.status);
            if (!response.ok) {
                const text = await response.text();
                console.error('[AuthService] Token exchange error response:', text);
                try {
                    const error = JSON.parse(text);
                    console.error('[AuthService] Parsed error:', error);
                }
                catch (e) {
                    // Response wasn't JSON
                }
                return false;
            }
            const data = await response.json();
            console.log('[AuthService] Token exchange successful, storing tokens');
            console.log('[AuthService] Access token:', data.data?.access_token?.substring(0, 20) + '...');
            // Extract tokens from the API response wrapper
            const accessToken = data.data?.access_token || data.access_token;
            const refreshToken = data.data?.refresh_token || data.refresh_token;
            this.setTokens(accessToken, refreshToken);
            // Clear the code verifier
            localStorage.removeItem(CODE_VERIFIER_KEY);
            console.log('[AuthService] Cleared code verifier');
            return true;
        }
        catch (error) {
            console.error('[AuthService] Token exchange failed:', error);
            return false;
        }
    }
    /**
     * Store tokens in localStorage
     */
    static setTokens(accessToken, refreshToken) {
        localStorage.setItem(TOKEN_KEY, accessToken);
        if (refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        }
    }
    /**
     * Get the access token
     */
    static getAccessToken() {
        return localStorage.getItem(TOKEN_KEY);
    }
    /**
     * Get the refresh token
     */
    static getRefreshToken() {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    }
    /**
     * Store user info
     */
    static setUser(user) {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
    /**
     * Get stored user info
     */
    static getUser() {
        const userStr = localStorage.getItem(USER_KEY);
        if (!userStr)
            return null;
        try {
            return JSON.parse(userStr);
        }
        catch {
            return null;
        }
    }
    /**
     * Clear all auth data
     */
    static clearAuth() {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
    }
    /**
     * Check if user is authenticated (has token)
     */
    static isAuthenticated() {
        return !!this.getAccessToken();
    }
    /**
     * Validate token with backend /whoami endpoint
     */
    static async validateToken() {
        const token = this.getAccessToken();
        if (!token)
            return null;
        try {
            const apiUrl = import.meta.env.VITE_API_URL;
            const response = await fetch(`${apiUrl}/whoami`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                if (response.status === 401) {
                    this.clearAuth();
                }
                return null;
            }
            const data = await response.json();
            if (data.data) {
                this.setUser(data.data);
                return data.data;
            }
            return null;
        }
        catch (error) {
            console.error('Token validation error:', error);
            return null;
        }
    }
}
//# sourceMappingURL=authService.js.map